import { 
  CreditCardIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ShieldCheckIcon
} from '../Icons'

interface PaymentScreenProps {
  onBack: () => void
  onConfirm: () => void
  onLog?: (message: string) => void
  totalAmount?: number
  itemsCount?: number
}

const colors = {
  bg: '#050508',
  cardBg: '#141418',
  nestedBg: '#1c1c22',
  accent: '#9333ea',
  accentLight: '#a855f7',
  accentBg: 'rgba(147, 51, 234, 0.12)',
  accentBorder: 'rgba(147, 51, 234, 0.25)',
  cardBorder: 'rgba(255, 255, 255, 0.12)',
  text: '#ffffff',
  textMuted: 'rgba(255, 255, 255, 0.52)',
  green: '#22c55e',
  yellow: '#fbbf24',
  blue: '#3b82f6',
}

export default function PaymentScreen({ 
  onLog, 
  onBack, 
  onConfirm,
  totalAmount = 0,
  itemsCount = 0
}: PaymentScreenProps) {
  const handlePayment = () => {
    onLog?.(`💳 Тестовая оплата: ${totalAmount} ₽`)
    setTimeout(() => {
      onConfirm()
    }, 1000)
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: colors.bg,
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Шапка */}
      <div style={{
        background: colors.cardBg,
        borderBottom: `1px solid ${colors.cardBorder}`,
        padding: '16px 20px',
        position: 'sticky',
        top: 0,
        zIndex: 100,
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.accent,
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: 0,
            }}
          >
            <ChevronLeftIcon size={20} />
            Назад
          </button>

          <div style={{
            color: colors.text,
            fontSize: '16px',
            fontWeight: 600,
            flex: 1,
            textAlign: 'center',
            marginRight: '60px',
          }}>
            Оплата заказа
          </div>
        </div>
      </div>

      {/* Контент */}
      <div style={{
        padding: '24px 20px',
      }}>
        {/* Информация о заказе */}
        <div style={{
          background: `linear-gradient(135deg, ${colors.accent}20, transparent)`,
          border: `1px solid ${colors.accentBorder}`,
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px',
        }}>
          <div style={{
            color: colors.textMuted,
            fontSize: '12px',
            marginBottom: '8px',
          }}>
            К оплате
          </div>
          <div style={{
            color: colors.text,
            fontSize: '36px',
            fontWeight: 700,
            marginBottom: '12px',
          }}>
            {totalAmount.toLocaleString('ru-RU')} ₽
          </div>
          <div style={{
            color: colors.textMuted,
            fontSize: '13px',
          }}>
            {itemsCount} {itemsCount === 1 ? 'товар' : itemsCount < 5 ? 'товара' : 'товаров'} в заказе
          </div>
        </div>

        {/* Способ оплаты */}
        <div style={{
          marginBottom: '24px',
        }}>
          <div style={{
            color: colors.text,
            fontSize: '14px',
            fontWeight: 600,
            marginBottom: '12px',
          }}>
            Способ оплаты
          </div>

          {/* Тестовая карта */}
          <div style={{
            background: colors.cardBg,
            border: `1px solid ${colors.accentBorder}`,
            borderRadius: '12px',
            padding: '16px',
            cursor: 'pointer',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
            }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '10px',
                background: `${colors.blue}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CreditCardIcon size={24} color={colors.blue} />
              </div>

              <div style={{ flex: 1 }}>
                <div style={{
                  color: colors.text,
                  fontSize: '14px',
                  fontWeight: 600,
                  marginBottom: '4px',
                }}>
                  Тестовая карта
                </div>
                <div style={{
                  color: colors.textMuted,
                  fontSize: '12px',
                }}>
                  •••• 4242
                </div>
              </div>

              <CheckCircleIcon size={20} color={colors.green} />
            </div>
          </div>
        </div>

        {/* Безопасность */}
        <div style={{
          background: colors.cardBg,
          border: `1px solid ${colors.cardBorder}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}>
          <ShieldCheckIcon size={24} color={colors.green} />
          <div>
            <div style={{
              color: colors.text,
              fontSize: '13px',
              fontWeight: 600,
              marginBottom: '2px',
            }}>
              Безопасная оплата
            </div>
            <div style={{
              color: colors.textMuted,
              fontSize: '11px',
            }}>
              Тестовый режим • Карта не будет списана
            </div>
          </div>
        </div>

        {/* Кнопка оплаты */}
        <button
          onClick={handlePayment}
          style={{
            width: '100%',
            background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentLight})`,
            border: 'none',
            borderRadius: '12px',
            padding: '16px',
            color: colors.text,
            fontSize: '16px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            boxShadow: `0 4px 16px ${colors.accent}40`,
          }}
        >
          <CreditCardIcon size={20} />
          Оплатить {totalAmount.toLocaleString('ru-RU')} ₽
        </button>

        {/* Подсказка */}
        <div style={{
          marginTop: '16px',
          padding: '12px',
          background: colors.nestedBg,
          borderRadius: '8px',
          textAlign: 'center',
        }}>
          <div style={{
            color: colors.textMuted,
            fontSize: '11px',
            lineHeight: '1.5',
          }}>
            💡 Это тестовая страница оплаты.<br />
            После нажатия кнопки заказ будет сохранён.
          </div>
        </div>
      </div>
    </div>
  )
}
